import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Workflow,
  FileText,
  Layers,
  Users,
  ClipboardList,
  Wrench,
} from "lucide-react";
import Navbar from "../../../sub-comp/Navbar";
import sadImg from "../../../../assets/Data Analysis.jpeg";
import FullScreenImage from "../../../Home/ShowcaseSection";
import Footer from "../../../sub-comp/MainFooter";

const SystemAnalysisDesign = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [visibleModules, setVisibleModules] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);

    const moduleTimer = setTimeout(() => {
      const modules = Array.from({ length: 10 }, (_, i) => i);
      modules.forEach((index) => {
        setTimeout(() => {
          setVisibleModules((prev) => [...prev, index]);
        }, index * 120);
      });
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearTimeout(moduleTimer);
    };
  }, []);

  // Learning Items
  const learningItems = [
    {
      icon: <Workflow className="w-8 h-8 text-custom-pink mb-4" />,
      title: "System Development Life Cycle (SDLC)",
      description:
        "Understand different phases of SDLC including planning, analysis, design, implementation, and maintenance.",
    },
    {
      icon: <FileText className="w-8 h-8 text-custom-pink mb-4" />,
      title: "Requirements Gathering",
      description:
        "Learn to collect, document, and analyze user requirements effectively.",
    },
    {
      icon: <Layers className="w-8 h-8 text-custom-pink mb-4" />,
      title: "System Modeling",
      description:
        "Use diagrams, flowcharts, and modeling tools like UML to represent system processes.",
    },
    {
      icon: <Users className="w-8 h-8 text-custom-pink mb-4" />,
      title: "Stakeholder Analysis",
      description:
        "Identify and manage different stakeholders throughout system development.",
    },
    {
      icon: <ClipboardList className="w-8 h-8 text-custom-pink mb-4" />,
      title: "Design Techniques",
      description:
        "Explore structured design, object-oriented design, and prototyping approaches.",
    },
    {
      icon: <Wrench className="w-8 h-8 text-custom-pink mb-4" />,
      title: "Testing & Maintenance",
      description:
        "Learn testing methods, validation, verification, and post-deployment support.",
    },
  ];

  // Course Modules
  const modules = [
    "Introduction to System Analysis & Design",
    "System Development Life Cycle (SDLC)",
    "Requirements Gathering & Analysis",
    "System Modeling with UML",
    "Feasibility Study & Risk Analysis",
    "Process & Data Modeling",
    "System Design Principles",
    "Implementation & Testing",
    "Maintenance & Evaluation",
    "Capstone Project: System Design Case Study",
  ];

  return (
    <div>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="relative h-[42rem] bg-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={sadImg}
              alt="System Analysis & Design"
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          </div>
          <div className="relative z-10 flex items-center justify-center h-full">
            <div
              className={`text-center text-white transform transition-all duration-1000 ease-out ${
                isVisible
                  ? "translate-y-0 opacity-100"
                  : "translate-y-8 opacity-0"
              }`}
            >
              <h1 className="text-5xl md:text-6xl font-bold text-red-100 animate-pulse">
                System Analysis & Design
              </h1>
              <p
                className={`text-xl mt-4 opacity-90 transform transition-all duration-1000 delay-300 ${
                  isVisible
                    ? "translate-y-0 opacity-90"
                    : "translate-y-4 opacity-0"
                }`}
              >
                Learn how to design, analyze, and implement efficient systems
              </p>
            </div>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <Link
              to="/course/information-technology"
              className="flex items-center text-custom-pink hover:text-custom-pink/70 font-medium transition-all duration-300 hover:translate-x-1 group"
            >
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-300" />
              Back to IT Programs
            </Link>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-3 gap-12">
            {/* Main Info */}
            <div className="md:col-span-2">
              {/* Program Overview */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-800 mb-6 relative">
                  Program Overview
                  <span className="absolute bottom-0 left-0 w-16 h-1 bg-custom-pink"></span>
                </h2>
                <p className="text-gray-600 mb-4">
                  This program provides students with the knowledge and skills
                  to analyze, design, and implement information systems
                  effectively.
                </p>
                <p className="text-gray-600">
                  You’ll explore SDLC, system modeling, requirements
                  engineering, and stakeholder management to ensure successful
                  system development.
                </p>
              </section>

              {/* What You'll Learn */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-800 mb-6 relative">
                  What You'll Learn
                  <span className="absolute bottom-0 left-0 w-16 h-1 bg-custom-pink"></span>
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {learningItems.map((item, index) => (
                    <div
                      key={index}
                      className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 group cursor-pointer"
                    >
                      <div className="transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                        {item.icon}
                      </div>
                      <h3 className="text-xl font-semibold mb-3 group-hover:text-custom-pink">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 group-hover:text-gray-700">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Course Modules */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-800 mb-6 relative">
                  Course Modules
                  <span className="absolute bottom-0 left-0 w-16 h-1 bg-custom-pink"></span>
                </h2>
                <div className="space-y-4">
                  {modules.map((module, index) => (
                    <div
                      key={index}
                      className={`bg-white p-4 rounded-lg shadow-sm border-l-4 border-custom-pink hover:shadow-md hover:border-l-8 transition-all duration-500 cursor-pointer ${
                        visibleModules.includes(index)
                          ? "opacity-100"
                          : "opacity-0"
                      }`}
                    >
                      <h3 className="font-semibold text-gray-800 group-hover:text-custom-pink">
                        Module {index + 1}: {module}
                      </h3>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div>
              {/* Program Details */}
              <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  Program Details
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li>
                    <span className="font-semibold">Duration:</span> 5 months
                  </li>
                  <li>
                    <span className="font-semibold">Format:</span> Theory + Case
                    Studies
                  </li>
                  <li>
                    <span className="font-semibold">Schedule:</span> Flexible
                  </li>
                  <li>
                    <span className="font-semibold">Prerequisites:</span> Basic
                    programming or IT knowledge
                  </li>
                </ul>
              </div>

              {/* Career Opportunities */}
              <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  Career Opportunities
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Systems Analyst</li>
                  <li>• Business Analyst</li>
                  <li>• IT Consultant</li>
                  <li>• Project Manager</li>
                  <li>• Requirements Engineer</li>
                  <li>• System Designer</li>
                </ul>
              </div>

              {/* Call To Action */}
              <div className="bg-custom-pink text-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-4">Ready to Start?</h3>
                <p className="mb-4">
                  Enroll in System Analysis & Design to learn how to plan and
                  build effective systems for organizations.
                </p>
                <button className="w-full bg-yellow-500 text-black py-3 px-6 rounded-lg font-semibold hover:bg-yellow-600 transition mb-3">
                  Apply Now
                </button>
                <button className="w-full border-2 border-white py-3 px-6 rounded-lg font-semibold hover:bg-white hover:text-custom-pink transition">
                  Request Info
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FullScreenImage />
      <Footer />
    </div>
  );
};

export default SystemAnalysisDesign;
